import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { NotificationBell } from "@/components/NotificationBell";
import { User, Settings, LogOut, Menu, X, Sparkles, FileText, Search, Plane, Zap, Users } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import PartnerLogoutModal from "@/components/PartnerLogoutModal";
import { useAuthReady } from "@/hooks/useAuthReady";
import { useNavMeta } from "@/hooks/useNavMeta";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";

const Navigation = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPartnerLogout, setShowPartnerLogout] = useState(false);
  const { plan } = useSubscription();

  const { user: authUser, isReady: sharedAuthReady } = useAuthReady();
  const { data: navMeta } = useNavMeta();

  const isAuthenticated = sharedAuthReady ? !!authUser : null;
  const userEmail = authUser?.email || "";
  const userName = navMeta?.userName || authUser?.email?.split("@")[0] || "User";
  const isAdmin = navMeta?.isAdmin || false;
  const isPartnerUser = navMeta?.isPartner || false;

  
  
  const isActive = (path: string) => location.pathname === path;

  // Sign-out is handled by the shared AuthProvider — no local listener needed

  const handleSignOut = async () => {
    // If partner, show feedback modal first
    if (isPartnerUser) {
      setShowPartnerLogout(true);
      return;
    }
    await performSignOut();
  };

  const performSignOut = async () => {
    setShowPartnerLogout(false);
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "Signed out successfully" });
      window.location.href = "/";
    }
  };

  return (
    <>
    <nav className="fixed top-0 w-full z-50 border-b border-border bg-white/80 backdrop-blur-lg shadow-soft">
      <div className="container mx-auto px-2 md:px-4">
        <div className="flex items-center justify-between h-[4.8rem]">
          {/* Logo - always on left */}
          <Link to="/" className="flex items-center gap-1 group">
            <span className="text-[6px] font-extrabold text-primary-foreground bg-primary rounded-sm px-[2px] py-[3px] tracking-wider select-none" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>
              BETA
            </span>
            <div className="flex flex-col" style={{ width: 'fit-content' }}>
              <span className="text-xl font-black text-foreground leading-none flex justify-between w-full" aria-label="ARROWLINK">
                {'ARROWLINK'.split('').map((char, i) => <span key={i}>{char}</span>)}
              </span>
              <span className="text-[0.5rem] font-normal text-primary leading-none mt-0.5 flex justify-between w-full" aria-label="LIVING CONNECTIONS">
                {'LIVING CONNECTIONS'.split('').map((char, i) => <span key={i}>{char === ' ' ? '\u00A0' : char}</span>)}
              </span>
            </div>
          </Link>

          <div className="md:hidden flex items-center gap-2">
            {sharedAuthReady && isAuthenticated && (
              <>
                {(userEmail === "demo@arrowlink.ai") && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-800 rounded-full">
                    Demo
                  </span>
                )}
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full h-8 w-8" aria-label="Open account menu">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium leading-none capitalize">{userName}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/settings?tab=profile" className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('nav.profile')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/templates" className="cursor-pointer">
                        <FileText className="mr-2 h-4 w-4" />
                        <span>{t('nav.templates')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>{t('nav.settings')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('nav.signOut')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            
            {/* Burger menu - now to the right of profile icon */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-11 w-11" aria-label="Open main menu">
                  <Menu className="h-[1.90rem] w-[1.90rem]" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-[320px] p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b border-border">
                    <div className="flex flex-col" style={{ width: 'fit-content' }}>
                      <span className="text-xl font-black text-foreground leading-none flex justify-between w-full" aria-label="ARROWLINK">
                        {'ARROWLINK'.split('').map((char, i) => <span key={i}>{char}</span>)}
                      </span>
                      <span className="text-[0.5rem] font-normal text-primary leading-none mt-0.5 flex justify-between w-full" aria-label="LIVING CONNECTIONS">
                        {'LIVING CONNECTIONS'.split('').map((char, i) => <span key={i}>{char === ' ' ? '\u00A0' : char}</span>)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Primary CTA at top */}
                  {sharedAuthReady && isAuthenticated ? (
                    <div className="p-4 border-b border-border">
                      <SheetClose asChild>
                        <Link to="/dashboard">
                          <Button variant="gradient" size="sm" className="w-full rounded-full">
                            {t('nav.dashboard')}
                          </Button>
                        </Link>
                      </SheetClose>
                    </div>
                  ) : sharedAuthReady ? (
                    <div className="p-4 border-b border-border">
                      <SheetClose asChild>
                        <Link to="/auth">
                          <Button variant="gradient" size="sm" className="w-full rounded-full">
                            {t('nav.signUp')}
                          </Button>
                        </Link>
                      </SheetClose>
                    </div>
                  ) : null}
                  
                  <div className="flex-1 py-4">
                    <div className="flex flex-col gap-1 px-2">
                      {sharedAuthReady && !isAuthenticated && (
                        <SheetClose asChild>
                          <Link to="/" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                            {t('nav.home')}
                          </Link>
                        </SheetClose>
                      )}
                      <SheetClose asChild>
                        <Link to="/network" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/network') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.network')}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/leads" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/leads') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.leads')}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/pilot" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/pilot') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.pilot')}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/qurrent" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/qurrent') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.qurrent', 'Qurrent')}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/ai-search" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/ai-search') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.arrow', 'Arrow')}
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link to="/team" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/team') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                          {t('nav.team', 'Team')}
                        </Link>
                      </SheetClose>
                      {sharedAuthReady && !isAuthenticated && (
                        <SheetClose asChild>
                          <Link to="/pricing" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/pricing') ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                            {t('nav.pricing', 'Pricing')}
                          </Link>
                        </SheetClose>
                      )}
                      {isAdmin && (
                        <SheetClose asChild>
                          <Link to="/admin" className={`flex items-center gap-3 px-3 py-3.5 text-base font-medium rounded-md transition-colors ${isActive('/admin') ? 'text-primary bg-primary/10 font-semibold' : 'text-primary hover:text-primary/80 hover:bg-muted'}`}>
                            {t('nav.admin')}
                          </Link>
                        </SheetClose>
                      )}
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
          
          {/* Desktop navigation - only show when auth state is resolved */}
          <div className="hidden md:flex items-center gap-8">
            {sharedAuthReady ? (
              <>
                {!isAuthenticated && (
                  <Link to="/" className={`text-sm font-medium nav-link-hover ${isActive('/') ? 'text-primary' : 'text-muted-foreground'}`}>
                    {t('nav.home')}
                  </Link>
                )}
                <Link to="/network" className={`text-sm font-medium nav-link-hover ${isActive('/network') ? 'text-primary' : 'text-muted-foreground'}`}>
                  {t('nav.network')}
                </Link>
                <Link 
                  to="/leads" 
                  data-tour="nav-leads"
                  className={`text-sm font-medium nav-link-hover ${isActive('/leads') ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {t('nav.leads')}
                </Link>
                <Link
                  to="/pilot" 
                  data-tour="nav-pilot"
                  className={`text-sm font-medium nav-link-hover ${isActive('/pilot') ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {t('nav.pilot')}
                </Link>
                <Link 
                  to="/qurrent" 
                  className={`text-sm font-medium nav-link-hover ${isActive('/qurrent') ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {t('nav.qurrent', 'Qurrent')}
                </Link>
                <Link 
                  to="/ai-search" 
                  data-tour="nav-ai-search"
                  className={`text-sm font-medium nav-link-hover ${isActive('/ai-search') ? 'text-primary' : 'text-muted-foreground'}`}
                >
                  {t('nav.arrow', 'Arrow')}
                </Link>
                <Link to="/team" className={`text-sm font-medium nav-link-hover ${isActive('/team') ? 'text-primary' : 'text-muted-foreground'}`}>
                  {t('nav.team', 'Team')}
                </Link>
                {!isAuthenticated && (
                  <Link to="/pricing" className={`text-sm font-medium nav-link-hover ${isActive('/pricing') ? 'text-primary' : 'text-muted-foreground'}`}>
                    {t('nav.pricing', 'Pricing')}
                  </Link>
                )}
                {isAdmin && (
                  <Link to="/admin" className={`text-sm font-medium transition-colors ${isActive('/admin') ? 'text-primary font-semibold' : 'text-primary hover:text-primary/80'}`}>
                    {t('nav.admin')}
                  </Link>
                )}
              </>
            ) : null}
          </div>

          {/* Desktop: Right side actions */}
          <div className="hidden md:flex items-center gap-3">
            {!sharedAuthReady ? null : isAuthenticated ? (
              <>
                {(userEmail === "demo@arrowlink.ai") && (
                  <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                    {t('nav.demoMode', 'Demo Mode')}
                  </span>
                )}
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-full h-9 w-9">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-background z-50">
                    <DropdownMenuLabel className="font-normal">
                      <p className="text-sm font-medium leading-none capitalize">{userName}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/settings?tab=profile" className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('nav.profile')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/templates" className="cursor-pointer">
                        <FileText className="mr-2 h-4 w-4" />
                        <span>{t('nav.templates')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        <span>{t('nav.settings')}</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>{t('nav.signOut')}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/dashboard" data-tour="nav-dashboard">
                  <Button variant="gradient" size="sm" className="rounded-full">
                    {t('nav.dashboard')}
                  </Button>
                </Link>
              </>
            ) : (
              <Link to="/auth">
                <Button variant="gradient" size="sm" className="rounded-full">
                  Sign In
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
    {showPartnerLogout && (
      <PartnerLogoutModal
        open={showPartnerLogout}
        onClose={() => setShowPartnerLogout(false)}
        onConfirmLogout={performSignOut}
      />
    )}
    </>
  );
};

export default Navigation;
